/* eslint @typescript-eslint/no-explicit-any: 0 */
/** NOTE: while we don't have the Angelou Table component typed, we need to use a few any definitions here */

import { ReactElement } from "react";

import { LoadingSpinner, Table } from "@newsela/angelou";
import { isEmpty } from "@s-libs/micro-dash";

import { School } from "@/api/catalogs/mockData/schoolThroughUserMock";
import SchoolSelector from "@/directory/DirectoryTable/SchoolSelector/SchoolSelector";

import {
  directoryTableCss,
  filterAndDownloadCss,
  totalResultsStringCss,
  totalResultsWrapperCss,
} from "./DirectoryTable.style";
import Filter from "./Filter";
import NotFound from "./NotFound";

/* TODO - After updating Angelou Table to @tanstack/react-table v8, revisit this type and
 get TData from the generic type set while using the Table component. e.g Table<MyDataType> */
type TData = unknown;

export interface TanStackColumnDef {
  id?: string;
  accessor?: string;
  accessorFn?: (originalRow: TData, index: number) => any;

  columns?: TanStackColumnDef[];

  Header?:
    | string
    | ((props: {
        table: ReactElement;
        header: ReactElement;
        column: ReactElement;
      }) => unknown);

  Footer?:
    | string
    | ((props: {
        table: ReactElement;
        header: ReactElement;
        column: ReactElement;
      }) => unknown);

  Cell?: (cell: any) => unknown;
  disableSortBy?: boolean;
}

export interface DirectoryTableProps {
  data: any[];
  columns: TanStackColumnDef[];
  title: string;
  pageSize: number;
  loading?: boolean;
  filterUIProps: {
    searchFieldLabel: string;
    searchFieldPlaceholder: string;
  };
  filterDefinitions?: Record<string, any>;
  readQuerySearch?: boolean;
  onControlsUpdateCb?: (...args: any[]) => void;
  notFoundMessage?: ReactElement;
  placeholderMessage?: ReactElement | null;
  loadingMessage?: ReactElement;
  totalCount?: number;
  useManualUpdate?: boolean;
  initialState?: Record<string, any>;
  shouldDisplayTotalResultsString?: boolean;
  onChangeSchool?: (school: School | undefined | null) => void;
}

const DirectoryTable = ({
  title,
  columns,
  pageSize,
  filterUIProps,
  filterDefinitions,
  readQuerySearch,
  data,
  loading,
  loadingMessage,
  notFoundMessage,
  placeholderMessage,
  onControlsUpdateCb,
  totalCount,
  useManualUpdate = true,
  initialState = {},
  shouldDisplayTotalResultsString = false,
  onChangeSchool,
}: DirectoryTableProps): ReactElement => {
  const count = totalCount || 0;
  const hasFilterButton =
    !isEmpty(filterUIProps) && !isEmpty(filterDefinitions);
  const isNotFound = !loading && isEmpty(data) && notFoundMessage;
  const isDefaultLoading = loading && !loadingMessage;

  return (
    <>
      {onChangeSchool && <SchoolSelector onChangeSchool={onChangeSchool} />}
      <Table
        title={title}
        columns={columns}
        data={loading ? [] : data || []}
        initialState={{ pageSize, ...initialState }}
        useManualControls={useManualUpdate}
        onControlsUpdate={(
          controls: Record<string, any>,
          rawProps: Record<string, any>,
        ): void | boolean => {
          if (useManualUpdate && onControlsUpdateCb) {
            onControlsUpdateCb(controls, rawProps);
          } else {
            return false;
          }
        }}
        manualPagination={useManualUpdate}
        paginationOptions={[]}
        pageCount={Math.ceil(count / pageSize)}
        ariaLivePagination={(
          state: { pageIndex: number },
          tableProps: { pageOptions: string | any[] },
        ) => `page ${state.pageIndex + 1} of ${tableProps.pageOptions.length}`}
        generateTotalResultsString={(totalResultsCount: string) => {
          return shouldDisplayTotalResultsString && !loading ? (
            <div css={totalResultsWrapperCss}>
              <div css={totalResultsStringCss}>{totalResultsCount} results</div>
            </div>
          ) : null;
        }}
        generateAriaLiveFilter={({
          globalFilter,
        }: {
          filters: Record<string, unknown>;
          globalFilter: Record<string, unknown>;
        }) => {
          const hasSearch = globalFilter?.["searchBoxFilterValue"];
          return hasSearch && !loading ? `${count} results found` : "";
        }}
        __renderAboveTable={() => {
          if (loading && loadingMessage) return loadingMessage;
          return null;
        }}
        __renderBelowTable={() => {
          if (placeholderMessage) return placeholderMessage;
          if (isDefaultLoading) return <LoadingSpinner />;
          if (isNotFound) return notFoundMessage;
          return null;
        }}
        totalItems={count}
        __renderGlobalFilters={(filterProps: {
          setGlobalFilter: (filters: Record<string, any>) => void;
          globalFilter: Record<string, any>;
        }) => {
          // TODO: Remove the comment of the filter definitions validation until the backend implements filtering for the endpoints
          const renderComponent = !isEmpty(filterUIProps); // && !isEmpty(filterDefinitions);

          if (!filterDefinitions) {
            filterDefinitions = {};
          }

          return renderComponent ? (
            <div css={filterAndDownloadCss}>
              <Filter
                {...{
                  filterProps,
                  filterUIProps,
                  filterDefinitions,
                  readQuerySearch,
                }}
              />
            </div>
          ) : null;
        }}
        __cssFor={directoryTableCss({
          hasFilterButton,
          hideTable: Boolean(isNotFound || isDefaultLoading),
        })}
      />
    </>
  );
};

DirectoryTable.NotFound = NotFound;

export default DirectoryTable;
